import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Pencil, Upload, RefreshCw, RotateCcw, ExternalLink } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DOMAIN_ORDER } from "@/lib/sopUtils";
import { ManualCsvImport } from "@/components/manual/ManualCsvImport";

import { DocFeedbackAdmin } from "@/components/manual/DocFeedbackAdmin";
import { ReviewerEditsAdmin } from "@/components/manual/ReviewerEditsAdmin";
import { ReviewerPayAdmin } from "@/components/manual/ReviewerPayAdmin";
import { toast } from "sonner";


type Row = {
  id: string;
  doc_id: string;
  domain: string;
  title: string;
  version: string;
  status: string;
  requires_founder_review: boolean;
  sort_order: number | null;
  collection: string | null;
  human_edited: boolean | null;
  last_human_edit_at: string | null;
  slug: string | null;
};

export default function ManualAdmin() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [collection, setCollection] = useState<"sop" | "faq">("sop");
  const [humanOnly, setHumanOnly] = useState(false);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newFeedbackCount, setNewFeedbackCount] = useState(0);
  const [reviewerCheckedCount, setReviewerCheckedCount] = useState(0);
  const [linkHealthCount, setLinkHealthCount] = useState(0);
  const [progressDocIds, setProgressDocIds] = useState<Set<string>>(new Set());
  const [redoTarget, setRedoTarget] = useState<Row | null>(null);
  const [redoing, setRedoing] = useState(false);

  useEffect(() => {
    (async () => {
      const { count } = await supabase
        .from("doc_feedback" as any)
        .select("id", { count: "exact", head: true })
        .eq("status", "new");
      setNewFeedbackCount(count ?? 0);
      const { count: rc } = await supabase
        .from("doc_edit_proposals" as any)
        .select("id", { count: "exact", head: true })
        .eq("status", "checked");
      setReviewerCheckedCount(rc ?? 0);
      const { count: lc } = await supabase
        .from("doc_link_audit" as any)
        .select("id", { count: "exact", head: true })
        .eq("dismissed", false)
        .in("status", ["unresolved", "ambiguous", "target_unpublished", "dead_anchor"]);
      setLinkHealthCount(lc ?? 0);
    })();
  }, []);

  async function refreshIndex() {
    setRefreshing(true);
    try {
      const { data, error } = await supabase.functions.invoke("embed-docs", { body: {} });
      if (error) throw error;
      const r = data as any;
      toast.success(`Search index refreshed: ${r?.processed ?? 0} of ${r?.stale ?? 0} docs updated.`);
    } catch (e: any) {
      toast.error(`Refresh failed: ${e?.message || "unknown error"}`);
    } finally {
      setRefreshing(false);
    }
  }

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("sop_documents" as any)
      .select("id,doc_id,domain,title,version,status,requires_founder_review,sort_order,collection,human_edited,last_human_edit_at,slug")
      .order("domain", { ascending: true })
      .order("sort_order", { ascending: true });
    setRows(((data as any[]) || []) as Row[]);
    setLoading(false);
    // Load which docs have any reviewer progress rows (any reviewer).
    const { data: prog } = await supabase
      .from("reviewer_doc_progress" as any)
      .select("doc_id");
    const set = new Set<string>();
    for (const row of ((prog as any[]) || [])) set.add(row.doc_id);
    setProgressDocIds(set);
  }

  useEffect(() => { load(); }, []);

  async function sendBackToReviewer(row: Row) {
    setRedoing(true);
    try {
      const { error } = await supabase
        .from("reviewer_doc_progress" as any)
        .update({ status: "redo", updated_at: new Date().toISOString() })
        .eq("doc_id", row.doc_id);
      if (error) throw error;
      toast.success("Sent back to the reviewer's unfinished list.");
      setRedoTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Could not send back.");
    } finally {
      setRedoing(false);
    }
  }

  async function markReviewed(row: Row) {
    const { error } = await supabase
      .from("sop_documents" as any)
      .update({ requires_founder_review: false })
      .eq("id", row.id);
    if (error) {
      toast.error(error.message || "Could not mark as reviewed.");
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, requires_founder_review: false } : r)));
    toast.success("Marked as reviewed.");
  }

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const rc = (r.collection || "sop");
      if (rc !== collection) return false;
      if (domain !== "all" && r.domain !== domain) return false;
      if (status !== "all" && r.status !== status) return false;
      if (humanOnly && !r.human_edited) return false;
      if (needsReviewOnly && !r.requires_founder_review) return false;
      if (q.trim()) {
        const n = q.toLowerCase();
        if (!r.title.toLowerCase().includes(n) && !r.doc_id.toLowerCase().includes(n)) return false;
      }
      return true;
    });
  }, [rows, q, domain, status, collection, humanOnly, needsReviewOnly]);

  const needsReviewCount = useMemo(
    () => rows.filter((r) => (r.collection || "sop") === collection && r.requires_founder_review).length,
    [rows, collection],
  );


  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif font-semibold">Manual Admin</h1>
          <p className="text-sm text-muted-foreground">Manage Steward's Operations Manual documents.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refreshIndex} variant="outline" disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh search index
          </Button>
          <Button onClick={() => setShowImport((v) => !v)} variant={showImport ? "secondary" : "default"}>
            <Upload className="h-4 w-4 mr-2" /> {showImport ? "Hide" : "CSV Import"}
          </Button>
          <Button asChild variant="outline" className="gap-2">
            <Link to="/admin/link-health">
              Link Health
              {linkHealthCount > 0 && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{linkHealthCount}</Badge>
              )}
            </Link>
          </Button>
        </div>
      </div>

      {showImport && <ManualCsvImport onDone={load} />}

      <Tabs defaultValue="documents">
        <TabsList>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          
          <TabsTrigger value="feedback" className="gap-2">
            Doc Feedback
            {newFeedbackCount > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{newFeedbackCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reviewer-edits" className="gap-2">
            Reviewer Edits
            {reviewerCheckedCount > 0 && (
              <Badge className="text-[10px] px-1.5 py-0">{reviewerCheckedCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reviewer-pay">Reviewer Pay</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-4 mt-4">
          <Tabs value={collection} onValueChange={(v) => setCollection(v as "sop" | "faq")}>
            <TabsList>
              <TabsTrigger value="sop">Operations Manual</TabsTrigger>
              <TabsTrigger value="faq">Help Library</TabsTrigger>
            </TabsList>
          </Tabs>

          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input placeholder="Search doc_id or title…" value={q} onChange={(e) => setQ(e.target.value)} className="md:col-span-2" />
              <Select value={domain} onValueChange={setDomain}>
                <SelectTrigger><SelectValue placeholder="Domain" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All domains</SelectItem>
                  {DOMAIN_ORDER.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={humanOnly ? "default" : "outline"}
                onClick={() => setHumanOnly((v) => !v)}
              >
                {humanOnly ? "Showing human-edited only" : "Show human-edited only"}
              </Button>
              <Button
                size="sm"
                variant={needsReviewOnly ? "default" : "outline"}
                onClick={() => setNeedsReviewOnly((v) => !v)}
              >
                Needs review ({needsReviewCount})
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : (
              <div className="overflow-x-auto -mx-4 md:mx-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                      <th className="p-2">Doc</th>
                      <th className="p-2">Title</th>
                      <th className="p-2">Version</th>
                      <th className="p-2">Status</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-muted/40">
                        <td className="p-2 font-mono text-xs whitespace-nowrap">{r.doc_id}</td>
                        <td className="p-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{r.title}</span>
                            {r.requires_founder_review && (
                              <>
                                <Badge variant="outline" className="text-xs">founder review</Badge>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-[11px]"
                                  onClick={() => markReviewed(r)}
                                >
                                  Mark reviewed
                                </Button>
                              </>
                            )}
                            {r.human_edited && (
                              <Badge
                                variant="secondary"
                                className="text-xs"
                                title={r.last_human_edit_at ? `Last reviewer edit ${new Date(r.last_human_edit_at).toLocaleDateString()}` : undefined}
                              >
                                Human edited
                              </Badge>
                            )}
                          </div>
                          {r.human_edited && r.last_human_edit_at && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              reviewer edit {new Date(r.last_human_edit_at).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-xs">{r.version}</td>
                        <td className="p-2">
                          <Badge variant={r.status === "published" ? "default" : r.status === "retired" ? "secondary" : "outline"}>
                            {r.status}
                          </Badge>
                        </td>
                        <td className="p-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {progressDocIds.has(r.doc_id) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Send back to reviewer"
                                onClick={() => setRedoTarget(r)}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {r.status === "published" && (
                              <Button asChild size="sm" variant="ghost" title="Open live page">
                                <a
                                  href={`/support/library/${r.slug || r.doc_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                                </a>
                              </Button>
                            )}
                            <Button asChild size="sm" variant="ghost">
                              <Link to={`/admin/library/${r.doc_id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Link>
                            </Button>

                          </div>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No documents.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>


        <TabsContent value="feedback" className="mt-4">
          <DocFeedbackAdmin onCountChange={setNewFeedbackCount} />
        </TabsContent>

        <TabsContent value="reviewer-pay" className="mt-4">
          <ReviewerPayAdmin />
        </TabsContent>

        <TabsContent value="reviewer-edits" className="mt-4">
          <ReviewerEditsAdmin />
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!redoTarget} onOpenChange={(o) => !o && setRedoTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send back to reviewer?</AlertDialogTitle>
            <AlertDialogDescription>
              This returns the document to the reviewer's unfinished list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={redoing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={redoing}
              onClick={(e) => {
                e.preventDefault();
                if (redoTarget) sendBackToReviewer(redoTarget);
              }}
            >
              {redoing ? "Sending..." : "Send back"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

